import React from "react";
import { useNavigate } from "@tanstack/react-router";
import { AuthLayout } from "#app/components/auth/auth-layout";
import { Box, Button, Icon, Text } from "@salestrak/ui";

/** The mail-in-a-circle glyph the check-your-email screens center on. */
export function MailCircle() {
  return (
    <Box direction="row" justify="center">
      <Box
        width={72}
        height={72}
        rounded="full"
        background="muted"
        direction="column"
        align="center"
        justify="center"
      >
        <Icon name="mail" size="xl" color="muted" />
      </Box>
    </Box>
  );
}

type EmailSentScreenProps = {
  /** Names what was sent and to whom, e.g. "We've sent a verification link to …". */
  subheading: string;
  /** Re-send the email. `undefined` (no address to send to) disables the affordance. */
  onResend: (() => Promise<void>) | undefined;
};

/**
 * The "Check Your Email" screen: mail glyph, back-to-sign-in, and a resend
 * affordance. Shared by the verification-sent and reset-password-sent routes,
 * which differ only in copy and which resend endpoint they call.
 */
export function EmailSentScreen({ subheading, onResend }: EmailSentScreenProps) {
  const navigate = useNavigate();
  const [isResending, setIsResending] = React.useState(false);

  const handleResend = async () => {
    if (!onResend || isResending) return;
    setIsResending(true);
    try {
      await onResend();
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthLayout heading="Check Your Email" subheading={subheading}>
      <MailCircle />

      <Button onClick={() => navigate({ to: "/login" })}>
        Back to Sign In
      </Button>

      <Box direction="row" align="center" justify="center" gap="xs">
        <Text size="sm" variant="muted">
          Didn't receive it?
        </Text>
        <Button
          variant="link"
          size="sm"
          disabled={!onResend || isResending}
          onClick={() => void handleResend()}
        >
          {isResending ? "Resending..." : "Resend"}
        </Button>
      </Box>
    </AuthLayout>
  );
}
